/**
 * Content workflow statuses.
 *
 * MVP flow:
 *   GENERATING -> GENERATED -> REVIEW -> APPROVED -> SCHEDULED
 * Publishing flow:
 *   SCHEDULED -> PUBLISHING -> PUBLISHED | FAILED
 * Terminal:
 *   PUBLISHED, FAILED, ARCHIVED
 */
export const CONTENT_STATUSES = [
  'IDEA',
  'DRAFT',
  'GENERATING',
  'GENERATED',
  'REVIEW',
  'APPROVED',
  'SCHEDULED',
  'PUBLISHING',
  'PUBLISHED',
  'FAILED',
  'ARCHIVED',
] as const;

export type ContentStatus = (typeof CONTENT_STATUSES)[number];

export const CONTENT_STATUS_LABELS: Record<ContentStatus, string> = {
  IDEA: 'Idea',
  DRAFT: 'Draft',
  GENERATING: 'Generating',
  GENERATED: 'Generated',
  REVIEW: 'Waiting for review',
  APPROVED: 'Approved',
  SCHEDULED: 'Scheduled',
  PUBLISHING: 'Publishing',
  PUBLISHED: 'Published',
  FAILED: 'Failed',
  ARCHIVED: 'Archived',
};

/** UI badge tone per status. */
export const CONTENT_STATUS_TONES: Record<
  ContentStatus,
  'neutral' | 'info' | 'warning' | 'success' | 'danger'
> = {
  IDEA: 'neutral',
  DRAFT: 'neutral',
  GENERATING: 'info',
  GENERATED: 'info',
  REVIEW: 'warning',
  APPROVED: 'success',
  SCHEDULED: 'info',
  PUBLISHING: 'warning',
  PUBLISHED: 'success',
  FAILED: 'danger',
  ARCHIVED: 'neutral',
};

/**
 * Allowed status transitions. Anything not listed here is rejected by the
 * workflow service.
 */
export const CONTENT_TRANSITIONS: Record<ContentStatus, readonly ContentStatus[]> = {
  IDEA: ['DRAFT', 'GENERATING', 'ARCHIVED'],
  DRAFT: ['GENERATING', 'REVIEW', 'ARCHIVED'],
  GENERATING: ['GENERATED', 'DRAFT', 'FAILED'],
  GENERATED: ['REVIEW', 'DRAFT', 'ARCHIVED'],
  REVIEW: ['APPROVED', 'DRAFT', 'ARCHIVED'],
  APPROVED: ['SCHEDULED', 'REVIEW', 'ARCHIVED'],
  SCHEDULED: ['PUBLISHING', 'APPROVED', 'FAILED', 'ARCHIVED'],
  PUBLISHING: ['PUBLISHED', 'FAILED'],
  PUBLISHED: ['ARCHIVED'],
  FAILED: ['DRAFT', 'REVIEW', 'SCHEDULED', 'ARCHIVED'],
  ARCHIVED: [],
};

export const TERMINAL_CONTENT_STATUSES: readonly ContentStatus[] = [
  'PUBLISHED',
  'ARCHIVED',
];

export function canTransition(from: ContentStatus, to: ContentStatus): boolean {
  return CONTENT_TRANSITIONS[from].includes(to);
}

export function isTerminalStatus(status: ContentStatus): boolean {
  return TERMINAL_CONTENT_STATUSES.includes(status);
}
